import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Users, PhoneCall, CalendarCheck, TrendingUp, AlertTriangle,
  CheckCircle, Megaphone, Trash2, Check, X, Filter, BellRing
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const NOTIF_ICON = {
  new_lead: { icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
  lead_assigned: { icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  lead_reassigned: { icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
  followup_due: { icon: PhoneCall, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  followup_overdue: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  site_visit_scheduled: { icon: CalendarCheck, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  site_visit_completed: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  client_converted: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  deal_closed: { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  task_assigned: { icon: CalendarCheck, color: 'text-amber-600', bg: 'bg-amber-50' },
  task_completed: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  system: { icon: Megaphone, color: 'text-slate-600', bg: 'bg-slate-50' },
  reassignment_needed: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
};

const getTimeAgo = (d) => {
  if (!d) return '';
  const h = Math.floor((Date.now() - new Date(d)) / (1000 * 60 * 60));
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
};

const NotificationBell = ({ position = 'bottom' }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe, needsIOSInstall } = usePushNotifications();

  const handlePushToggle = async () => {
    if (isSubscribed) {
      const ok = await unsubscribe();
      if (ok) toast.success('Push notifications disabled');
    } else {
      const ok = await subscribe();
      if (ok) toast.success('Push notifications enabled!');
      else if (permission === 'denied') toast.error('Notifications blocked by browser. Please allow in browser settings.');
      else toast.error('Could not enable push notifications');
    }
  };

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, filter]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchUnread = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch { /* silent */ }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get(`/notifications?filter_type=${filter}`);
      setNotifications(res.data);
    } catch { /* silent */ }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await api.post(`/notifications/${notif.id}/read`).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
    else if (notif.lead_id) navigate('/leads');
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch { /* silent */ }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      fetchUnread();
    } catch { /* silent */ }
  };

  const FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'leads', label: 'Leads' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'deals', label: 'Deals' },
    { value: 'system', label: 'System' },
  ];

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm" data-testid="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className={`absolute ${position === 'top' ? 'right-0 top-full mt-2' : 'left-0 bottom-full mb-2'} w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in`} data-testid="notification-panel">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button className="text-[11px] font-semibold text-[#B89B72] hover:underline" onClick={handleMarkAllRead} data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
              <button className="p-1 rounded hover:bg-slate-100" onClick={() => setOpen(false)}>
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Push Toggle */}
          {isSupported && (
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BellRing className={`w-3.5 h-3.5 ${isSubscribed ? 'text-emerald-500' : 'text-slate-400'}`} />
                <span className="text-[11px] text-slate-500 font-medium">Push alerts</span>
              </div>
              <button
                className={`relative w-9 h-5 rounded-full transition-colors ${isSubscribed ? 'bg-emerald-500' : 'bg-slate-300'}`}
                onClick={handlePushToggle}
                data-testid="push-toggle"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isSubscribed ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
          )}

          {/* iOS PWA Install Guidance */}
          {needsIOSInstall && (
            <div className="px-4 py-2.5 border-b border-slate-100 bg-amber-50" data-testid="ios-install-guide">
              <p className="text-[11px] text-amber-800 font-medium">Add Aavaas CRM to your Home Screen to enable push notifications.</p>
              <p className="text-[10px] text-amber-600 mt-0.5">Tap the Share button in Safari, then &quot;Add to Home Screen&quot;.</p>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-1 px-3 py-2 border-b border-slate-50 overflow-x-auto">
            {FILTERS.map(f => (
              <button
                key={f.value}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md whitespace-nowrap transition-colors ${filter === f.value ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">No notifications</p>
              </div>
            ) : notifications.map(n => {
              const iconCfg = NOTIF_ICON[n.type] || NOTIF_ICON.system;
              const Icon = iconCfg.icon;
              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-50 last:border-0 group ${n.read ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/30 hover:bg-blue-50/50'}`}
                  onClick={() => handleClick(n)}
                  data-testid={`notif-${n.id}`}
                >
                  <div className={`p-1.5 rounded-lg flex-shrink-0 mt-0.5 ${iconCfg.bg}`}>
                    <Icon className={`w-3.5 h-3.5 ${iconCfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm leading-snug ${n.read ? 'text-slate-600' : 'text-slate-900 font-semibold'}`}>{n.title}</p>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{getTimeAgo(n.created_at)}</p>
                  </div>
                  <button
                    className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all flex-shrink-0"
                    onClick={(e) => handleDelete(e, n.id)}
                  >
                    <Trash2 className="w-3 h-3 text-slate-300 hover:text-red-400" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
